/**
 * How the gallery reads the shot list (pure): runs, newest first; inside a
 * run one column per concept; under each first-round frame the chain of
 * refinements it spawned, oldest first — "the edited generation shows below
 * the original, and continues".
 *
 * Refinements carry their own `runId` (the server mints one per edit), so
 * they are attached by `parentId`, not by run. A refinement whose parent is
 * gone (deleted, or never synced) is shown as a root rather than dropped: a
 * frame someone paid for must stay visible.
 */
import type { ThumbShotInfo } from "@kola/shared";

export interface ThumbConceptColumn {
  /** The first-round frame. */
  root: ThumbShotInfo;
  /** Every refinement under it, in the order they were made (depth-first). */
  versions: ThumbShotInfo[];
}

export interface ThumbRunGroup {
  runId: string;
  /** When the run's first frame landed. */
  createdAt: number;
  columns: ThumbConceptColumn[];
}

export function groupThumbShots(shots: readonly ThumbShotInfo[]): ThumbRunGroup[] {
  const byId = new Map(shots.map((shot) => [shot.id, shot]));
  const children = new Map<string, ThumbShotInfo[]>();
  const roots: ThumbShotInfo[] = [];
  for (const shot of shots) {
    if (shot.parentId !== undefined && byId.has(shot.parentId)) {
      const list = children.get(shot.parentId) ?? [];
      list.push(shot);
      children.set(shot.parentId, list);
    } else {
      roots.push(shot);
    }
  }
  const byTime = (a: ThumbShotInfo, b: ThumbShotInfo) => a.createdAt - b.createdAt || a.id.localeCompare(b.id);

  const descendants = (id: string): ThumbShotInfo[] => {
    const out: ThumbShotInfo[] = [];
    for (const child of [...(children.get(id) ?? [])].sort(byTime)) {
      out.push(child, ...descendants(child.id));
    }
    return out;
  };

  const runs = new Map<string, ThumbRunGroup>();
  for (const root of roots) {
    let run = runs.get(root.runId);
    if (run === undefined) {
      run = { runId: root.runId, createdAt: root.createdAt, columns: [] };
      runs.set(root.runId, run);
    }
    run.createdAt = Math.min(run.createdAt, root.createdAt);
    run.columns.push({ root, versions: descendants(root.id) });
  }
  for (const run of runs.values()) {
    run.columns.sort((a, b) => a.root.concept - b.root.concept || byTime(a.root, b.root));
  }
  return [...runs.values()].sort((a, b) => b.createdAt - a.createdAt || b.runId.localeCompare(a.runId));
}
